import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Vendor } from '@/lib/schemas';

interface UseVendorsOptions {
  page?: number;
  limit?: number;
}

export function useVendors(options: UseVendorsOptions = {}) {
  const { page = 1, limit = 10 } = options;

  return useQuery<{data: Vendor[], totalCount: number}, Error>({
    queryKey: ['vendors', page, limit],
    queryFn: () => api.getVendors({ page, limit }),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useVendor(id: string) {
  return useQuery<Vendor | null, Error>({
    queryKey: ['vendors', id],
    queryFn: () => api.getVendor(id),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useCreateVendor() {
  const queryClient = useQueryClient();
  return useMutation<Vendor, Error, Omit<Vendor, 'vendor_id' | 'created_at' | 'updated_at'>>({
    mutationFn: (data) => api.createVendor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useUpdateVendor() {
  const queryClient = useQueryClient();
  return useMutation<Vendor | null, Error, { id: string; data: Partial<Omit<Vendor, 'vendor_id' | 'created_at'>>}>({
    mutationFn: ({ id, data }) => api.updateVendor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useDeleteVendor() {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, string>({
    mutationFn: (id) => api.deleteVendor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}
